import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useAuth } from '@/contexts/AuthContext';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag: (...args: any[]) => void;
  }
}

function hasGtagScript(): boolean {
  return Boolean(document.querySelector('script[src*="googletagmanager.com/gtag/js"]'));
}

export function AnalyticsDiagnostics() {
  const location = useLocation();
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const [lastTestAt, setLastTestAt] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const enabled = params.get('ga_debug') === '1';

  const gaId = settings.google_analytics_id;
  const gaEnabled = settings.google_analytics_enabled;
  const canonicalDomain = settings.canonical_domain;

  const isLovablePreview =
    window.location.hostname.includes('lovableproject.com') ||
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('webcontainer');

  const canonicalHost = (() => {
    try {
      return canonicalDomain ? new URL(canonicalDomain).hostname : '';
    } catch {
      return '';
    }
  })();

  const isOnCanonicalDomain = (() => {
    if (!canonicalHost) return true;
    const host = window.location.hostname;
    return host === canonicalHost || host === `www.${canonicalHost}`;
  })();

  const onAdminRoute = location.pathname.startsWith('/admin');
  const shouldLoadGA = Boolean(
    gaEnabled && gaId && !onAdminRoute && !isLovablePreview && isOnCanonicalDomain
  );

  const gtagPresent = typeof window !== 'undefined' && typeof window.gtag === 'function';
  const scriptPresent = typeof document !== 'undefined' ? hasGtagScript() : false;
  const authUserId = user?.id ? String(user.id) : null;

  const sendTestEvent = useCallback(() => {
    setLastError(null);

    if (!gtagPresent || !gaId) {
      setLastError('gtag not available (or GA Measurement ID missing).');
      return;
    }

    try {
      window.gtag?.('event', 'debug_test', {
        event_category: 'Diagnostics',
        page_path: location.pathname + location.search,
        transport_type: 'beacon',
      });
      setLastTestAt(new Date().toISOString());
    } catch (e: any) {
      setLastError(e?.message || 'Failed to send test event.');
    }
  }, [gtagPresent, gaId, location.pathname, location.search]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)]">
      <Card className="border border-border bg-background/95 backdrop-blur">
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Analytics Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <div className="text-muted-foreground">Route</div>
            <div className="truncate">{location.pathname}</div>

            <div className="text-muted-foreground">Host</div>
            <div className="truncate">{window.location.hostname}</div>

            <div className="text-muted-foreground">GA enabled</div>
            <div>{String(gaEnabled)}</div>

            <div className="text-muted-foreground">GA ID</div>
            <div className="truncate">{gaId || '(none)'}</div>

            <div className="text-muted-foreground">Preview env</div>
            <div>{String(isLovablePreview)}</div>

            <div className="text-muted-foreground">Admin route</div>
            <div>{String(onAdminRoute)}</div>

            <div className="text-muted-foreground">Canonical domain</div>
            <div className="truncate">{canonicalHost || '(not set)'}</div>

            <div className="text-muted-foreground">On canonical</div>
            <div>{String(isOnCanonicalDomain)}</div>

            <div className="text-muted-foreground">Should load GA</div>
            <div>{String(shouldLoadGA)}</div>

            <div className="text-muted-foreground">gtag()</div>
            <div>{String(gtagPresent)}</div>

            <div className="text-muted-foreground">gtag script</div>
            <div>{String(scriptPresent)}</div>

            <div className="text-muted-foreground">Auth user</div>
            <div className="truncate">{authUserId || '(logged out)'}</div>
          </div>

          {lastTestAt && (
            <div className="text-muted-foreground">Last test: {lastTestAt}</div>
          )}
          {lastError && (
            <div className="text-destructive">{lastError}</div>
          )}

          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={sendTestEvent}>
              Send test event
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.delete('ga_debug');
                window.location.href = url.toString();
              }}
            >
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
